import React, { useState } from "react";
import { Menu, X, ArrowRight, Trophy, ShieldCheck, Sparkles } from "lucide-react";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const brandLogo = `${process.env.PUBLIC_URL}/NuvioLogo.png?v=20260314c`;

  const navLinks = [
    {
      name: "About",
      href: "/home/about",
      children: [{ name: "Why Nuvio?", href: "/home/why-nuvio" }],
    },
    { name: "For Coaches", href: "/home/programs" },
    { name: "For Athletes", href: "/home/athletes" },
    { name: "Features", href: "/home/features" },
    { name: "Pricing", href: "/home/pricing" },
    { name: "Case Studies", href: "/home/case-studies" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <a href="/home" className="flex items-center gap-2">
          <img
            src={brandLogo}
            alt="Nuvio"
            className="w-8 h-8 rounded-lg object-cover shadow-sm"
          />
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Nuvio<span className="text-blue-600">Sport</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.name} className="relative group">
                <a
                  href={link.href}
                  className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors"
                >
                  {link.name}
                </a>
                <div className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all">
                  <div className="bg-white border border-slate-100 shadow-xl rounded-xl p-3 min-w-[180px]">
                    {link.children.map((child) => (
                      <a
                        key={child.name}
                        href={child.href}
                        className="block px-3 py-2 text-sm font-semibold text-slate-700 rounded-lg hover:bg-slate-50 hover:text-blue-600"
                      >
                        {child.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className={`text-sm font-semibold transition-colors ${
                  link.name === "About" ? "text-blue-600" : "text-slate-600 hover:text-blue-600"
                }`}
              >
                {link.name}
              </a>
            )
          )}
          <a href="/login" className="text-sm font-bold text-slate-700 hover:text-blue-600">
            Log In
          </a>
          <a
            href="/create-account"
            className="px-6 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
          >
            Create Account
          </a>
        </div>

        <button
          className="lg:hidden text-slate-700 p-2 hover:bg-slate-50 rounded-lg transition-colors"
          onClick={() => setIsOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div
        className={`lg:hidden absolute top-20 left-0 w-full bg-white border-b border-slate-100 transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-8 flex flex-col gap-6 shadow-xl">
          {navLinks.map((link) => (
            <div key={link.name} className="flex flex-col gap-2">
              <a
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="text-lg font-bold text-slate-900 hover:text-blue-600"
              >
                {link.name}
              </a>
              {link.children && (
                <div className="ml-4 flex flex-col gap-2">
                  {link.children.map((child) => (
                    <a
                      key={child.name}
                      href={child.href}
                      onClick={() => setIsOpen(false)}
                      className="text-sm font-semibold text-slate-600 hover:text-blue-600"
                    >
                      {child.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          <hr className="border-slate-100" />
          <a
            href="/login"
            onClick={() => setIsOpen(false)}
            className="w-full py-4 text-center font-bold text-slate-700 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
          >
            Log In
          </a>
          <a
            href="/create-account"
            onClick={() => setIsOpen(false)}
            className="w-full py-4 text-center font-bold text-white bg-blue-600 rounded-xl shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-all"
          >
            Create Account
          </a>
        </div>
      </div>
    </nav>
  );
};

export default function WhyNuvio() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      <section className="pt-36 pb-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest mb-6">
            Why Nuvio
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
            Built by a program director who needed answers, not guesswork.
          </h1>
          <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
            <p>
              After 10 years as a program director, I kept running into the same problem every season:
              We had a system… but we didn’t have every player ready to run it.
            </p>
            <p>Some athletes were ahead. Some were behind. Most were inconsistent.</p>
            <p>
              So instead of building on our system, we spent weeks just trying to make it functional.
            </p>
            <p className="text-slate-800 font-semibold">
              And that’s when it hit me: It wasn’t a coaching problem. It was a readiness problem.
            </p>
            <p>
              Because the issue wasn’t effort. The players actually wanted to get better. They asked
              for extra work. They were willing to train. They wanted coaching.
            </p>
            <p>
              But as a program director, I ran into a hard limit: I didn’t have the time to train
              everyone who wanted help… or the ability to map out individual plans for every player
              to make sure their work outside of practice actually aligned with what we needed as a team.
            </p>
            <p>And without that guidance, something predictable happened.</p>
            <p>Players still trained… just not in the right direction.</p>
            <p>They defaulted to:</p>
            <ul className="list-disc pl-6 space-y-1 text-slate-700">
              <li>Random drills</li>
              <li>Generic workouts</li>
              <li>Skills that didn’t translate to our system</li>
            </ul>
            <p>
              So now the problem compounded: Players weren’t ready… not because they weren’t working…
              but because their work wasn’t aligned.
            </p>
            <p className="text-slate-800 font-semibold">
              That’s when I realized something most programs miss: It’s not a talent problem.
              It’s not even a work ethic problem. It’s an alignment problem.
            </p>
            <p>
              Coaches have systems. Players have workouts. But nothing connects the two.
            </p>
            <p className="text-slate-800 font-semibold">So I built Nuvio Sport.</p>
            <p>
              A platform that takes your system—and turns it into a clear, individualized development
              plan for every player.
            </p>
            <p className="text-slate-800 font-semibold">Now:</p>
            <ul className="list-disc pl-6 space-y-1 text-slate-700">
              <li>Every player knows exactly what to work on</li>
              <li>Every workout ties back to your system</li>
              <li>And coaches don’t have to do it all themselves</li>
            </ul>
            <p>No more guessing who’s ready. No more wasted training. No more hoping it clicks mid-season.</p>
            <p className="text-slate-800 font-semibold">
              Because when players are training with alignment… You don’t just install your system. You execute it.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-slate-200 p-6 bg-white shadow-sm">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <Trophy size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Built for winning programs</h3>
            <p className="text-sm text-slate-600">
              Align every athlete to the same system so your team performs with consistency and confidence.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 p-6 bg-white shadow-sm">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <ShieldCheck size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Accountability baked in</h3>
            <p className="text-sm text-slate-600">
              Coaches can see progress, while athletes get a clear plan that keeps them on track.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 p-6 bg-white shadow-sm">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <Sparkles size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Custom improvement paths</h3>
            <p className="text-sm text-slate-600">
              Capture your priorities and let Nuvio organize individual development across your roster.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="/home/features"
            className="w-full sm:w-auto px-8 py-4 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-blue-600/20"
          >
            Explore Features <ArrowRight size={18} />
          </a>
          <a
            href="/create-account"
            className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-bold hover:bg-slate-50 transition-all"
          >
            Create Account
          </a>
        </div>
      </section>
    </div>
  );
}
